/**
 * Upgrade Screen - 3 category upgrade display
 * Shows all upgrades: purchasable, locked, and bought
 */
import { UPGRADES, isUpgradeUnlocked } from '../game/config/upgrades.js';
import { upgradeIconHtml } from './upgradeIcon.js';
import { formatNumber } from '../game/utils.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { onRender, getState, dispatch, setInnerHTML, requestRender, markScreenDirty } from './renderer.js';

let container: HTMLDivElement | null = null;

export function initUpgradeScreen() {
  const content = document.getElementById('screen-content');
  if (!content) return;

  container = document.createElement('div');
  container.id = 'upgrade-screen';
  container.className = 'screen-panel';
  content.appendChild(container);

  // Event delegation - survives innerHTML updates
  container.addEventListener('click', onContainerClick);

  onRender(renderUpgrades);
}

function onContainerClick(e: Event) {
  const target = (e.target as HTMLElement).closest('[data-upg]') as HTMLElement | null;
  if (!target) return;
  const upgId = target.dataset.upg!;
  const st = getState();
  const up = UPGRADES.find(u => u.id === upgId);
  if (!up) return;
  if (st.coins >= up.cost && !st.purchasedUpgrades.includes(upgId) && isUpgradeUnlocked(up, st)) {
    dispatch({ type: 'BUY_UPGRADE', payload: { upgradeId: upgId } });
    playSfx('purchase');
    markScreenDirty();
    requestRender();
  } else {
    playSfx('error');
  }
}

function renderUpgrades() {
  if (!container) return;
  const st = getState();
  const purchased = st.purchasedUpgrades;

  const categories = [
    { key: 'generator' as const, labelKey: 'upg_cat_generator', cls: 'cat-gold' },
    { key: 'global' as const, labelKey: 'upg_cat_global', cls: 'cat-cyan' },
    { key: 'research' as const, labelKey: 'upg_cat_research', cls: 'cat-purple' },
    { key: 'darkpool' as const, labelKey: 'upg_cat_darkpool', cls: 'cat-red' },
  ];

  let html = '';

  for (const cat of categories) {
    const catUps = UPGRADES.filter(u => u.category === cat.key);
    const boughtCount = catUps.filter(u => purchased.includes(u.id)).length;

    html += `<div class="upg-section">`;
    html += `<div class="upg-cat-title ${cat.cls}">${t(cat.labelKey)} (${boughtCount}/${catUps.length})</div>`;

    for (const up of catUps) {
      const isBought = purchased.includes(up.id);
      const isUnlocked = isUpgradeUnlocked(up, st);
      const name = t('upg_' + up.id) || up.name;
      const desc = t('upg_' + up.id + '_desc') || up.description;

      if (isBought) {
        // Purchased - compact chip
        html += `<span class="upg-chip ${cat.cls}"><span class="upg-chip-ic">${upgradeIconHtml(up)}</span> ${name}</span>`;
      } else if (isUnlocked) {
        // Available to buy
        const afford = st.coins >= up.cost;
        html += `<div class="gen-card${afford ? ' afford' : ''}" data-upg="${up.id}">
          <div class="upg-icon">${upgradeIconHtml(up)}</div>
          <div class="upg-info">
            <div class="upg-name">${name}</div>
            <div class="upg-desc">${desc}</div>
            <div class="upg-cost${afford ? ' upg-cost-green' : ' upg-cost-red'}">${formatNumber(up.cost)}</div>
          </div>
        </div>`;
      } else {
        // Locked
        html += `<div class="gen-card upg-locked-card">
          <div class="upg-icon" style="opacity:0.3">${upgradeIconHtml(up)}</div>
          <div class="upg-info">
            <div class="upg-name" style="opacity:0.4">${name}</div>
            <div class="upg-desc" style="opacity:0.3">${desc}</div>
            <div class="upg-cost upg-cost-locked">${getUnlockHint(up)}</div>
          </div>
        </div>`;
      }
    }

    html += `</div>`;
  }

  setInnerHTML(container, html);
}

function getUnlockHint(up: typeof UPGRADES[number]): string {
  const u = up.unlock;
  switch (u.type) {
    case 'generatorCount':
      return `<svg class="upg-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg> ${t('gen_' + u.generatorId) || u.generatorId} x${u.value}`;
    case 'totalEarned':
      return `<svg class="upg-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg> ${formatNumber(u.value)} ${t('settings_total_earned') || 'earned'}`;
    case 'prestigeCount':
      return `<svg class="upg-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg> ${t('prestige_count') || 'Prestige'} x${u.value}`;
    case 'cps':
      return `<svg class="upg-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg> CPS ${formatNumber(u.value)}`;
    case 'darkpoolBets':
      return `<svg class="upg-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg> ${t('dp_total_bets') || 'Bets'} x${u.value}`;
    default:
      return '<svg class="upg-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';
  }
}
